import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { IconBox, IconClockHour4, IconPhone, IconTruck } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { PageHeader } from '@hana/shell';
import { StateView, type ScreenState } from '@hana/ui';
import { DRIVER, DONE, nextLabel, progressLabel, statusOf, TRIPS, type Day, type MovTrip } from './trips';
import './movhome.i18n';
import s from './mov.module.css';

const DAYS: Day[] = ['yest', 'today', 'tmr'];
const DOT = ['var(--d-blue)', 'var(--d-amber)', 'var(--d-green)'];
const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];

function forcedState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
}

function TripCard({ t, day, picked, onPick }: { t: MovTrip; day: Day; picked: boolean; onPick: () => void }) {
  const tr = useT();
  const st = statusOf(t.p0);
  const next = nextLabel(t.p0, day);
  return (
    <div className={s.trip} style={picked ? { boxShadow: 'var(--sh), 0 0 0 2px var(--pri)' } : undefined}>
      <button type="button" className={s.open} aria-label={tr('mvh.open', { n: t.no })} onClick={onPick}>
        <div className={s.row}>
          <b className={s.no}>{t.no}</b>
          <span className={`${s.st} ${s[`tone-${st.tone}`]}`}>{st.label}</span>
        </div>
        <div className={s.sub}>
          <span title={tr('mvh.time', { v: t.time })} aria-label={tr('mvh.time', { v: t.time })}><IconClockHour4 size={15} stroke={1.8} aria-hidden="true" />{t.time}</span>
          <span title={`Cont ${t.cont}`} aria-label={`Cont ${t.cont}`}><IconBox size={15} stroke={1.8} aria-hidden="true" />{t.cont}</span>
          <span>{t.cus}</span>
        </div>
        <div className={s.pts}>
          {t.pts.map(([k, v], i) => (
            <div key={k} className={s.pt}>
              <i style={{ background: DOT[i] }} />
              <div><small>{k}</small><div>{v}</div></div>
            </div>
          ))}
        </div>
        <div className={s.segs} aria-hidden="true">
          {Array.from({ length: DONE }, (_, i) => <span key={i} data-on={i < t.p0 ? '1' : '0'} />)}
        </div>
        <div className={s.prog}>{progressLabel(t.p0, day)}</div>
      </button>
      {next && (
        <div className={s.nextWrap}>
          <button type="button" className={s.next} onClick={onPick}>{next}</button>
        </div>
      )}
    </div>
  );
}

/** Mint-Mov-Phone khung 2 "Hôm nay": danh sách chuyến của tài xế (ready / loading / empty / error / noperm). */
export function MovHome() {
  const tr = useT();
  const [day, setDay] = useState<Day>('today');
  const [picked, setPicked] = useState('t18');
  const navigate = useNavigate();
  const openTrip = (id: string) => { setPicked(id); void navigate({ to: '/mov/trip', search: { id } as never }); };
  const forced = forcedState();
  const list = TRIPS[day];
  const state: ScreenState = forced === 'ready' && list.length === 0 ? 'empty' : forced;

  return (
    <>
      <PageHeader title={tr('mvh.title')} />
      <div className={s.page}>
        <div className={s.date}>{tr('mvh.date')}</div>
        <div className={s.days} role="tablist" aria-label={tr('mvh.day')}>
          {DAYS.map((k) => (
            <button key={k} type="button" role="tab" aria-selected={day === k} className={`${s.day} ${day === k ? s.dayOn : ''}`} onClick={() => setDay(k)}>{tr(`mvh.d.${k}`)}</button>
          ))}
        </div>
        {state === 'error' && (
          <div role="status" className={s.net}><b>{tr('mvh.offB')}</b>{tr('mvh.offT')}</div>
        )}
        <StateView
          state={state === 'error' ? 'ready' : state}
          title={state === 'noperm' ? tr('mvh.npT') : tr('mvh.emT')}
          desc={state === 'noperm' ? tr('mvh.npD') : tr('mvh.emD')}
          icon={<IconTruck size={28} stroke={1.6} aria-hidden="true" />}
          skeleton={<>{[0, 1, 2].map((i) => <div key={i} className={s.sk} aria-hidden="true"><i style={{ width: '45%' }} /><i style={{ width: '85%' }} /><i style={{ width: '70%' }} /></div>)}</>}
        >
          <div className={s.duty}>
            <div className={s.dutyHd}>
              <span className={s.truck}><IconTruck size={26} stroke={1.9} aria-hidden="true" /></span>
              <div>
                <b className={s.plate}>{DRIVER.plate}</b>
                <div className={s.meta}>
                  <span title={tr('mvh.mooc', { v: DRIVER.mooc })}><IconTruck size={15} stroke={1.8} aria-hidden="true" />{DRIVER.mooc}</span>
                  <span title={DRIVER.shift}><IconClockHour4 size={15} stroke={1.8} aria-hidden="true" />{DRIVER.shift}</span>
                </div>
              </div>
            </div>
            <a className={s.call} href={`tel:${DRIVER.dutyPhone}`}><IconPhone size={20} stroke={2} aria-hidden="true" />{tr('mvh.call', { d: DRIVER.duty })}</a>
          </div>
          {list.map((t) => <TripCard key={t.id} t={t} day={day} picked={picked === t.id} onPick={() => openTrip(t.id)} />)}
        </StateView>
      </div>
    </>
  );
}
